import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { MoneyTextInput } from '@/components/ui/money-input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Coins, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import {
  buildBulkCostPlan,
  describeSkipped,
  type BulkCostMode,
  type BulkCostProduct,
} from '@/lib/bulkCost';
import { parseCostInput } from '@/lib/cost';
import { parsePercentInput } from '@/lib/pricing';

interface BulkCostModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ids selecionados na lista (sempre raízes). */
  selectedIds: string[];
  /** Catálogo plano — pais e variações. */
  products: BulkCostProduct[];
  onSuccess: () => void;
}

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/**
 * Define/ajusta o CUSTO de vários itens de uma vez.
 *
 * Existe porque o grupo 1 criou `products.cost` e nada preenchia em massa: sem
 * isto o custo entraria item a item e a base de margem/CMV dos grupos 5 e 6
 * nasceria vazia.
 *
 * A conta (inclusive a descida para as VARIAÇÕES, onde o custo realmente mora)
 * está em `src/lib/bulkCost.ts`, testada. Aqui só há UI, preview e I/O.
 */
export function BulkCostModal({ open, onOpenChange, selectedIds, products, onSuccess }: BulkCostModalProps) {
  const [mode, setMode] = useState<BulkCostMode>('set');
  const [amount, setAmount] = useState('');
  const [percent, setPercent] = useState('');
  const [direction, setDirection] = useState<'increase' | 'decrease'>('increase');
  const [saving, setSaving] = useState(false);

  const value = useMemo(() => {
    if (mode === 'set') return parseCostInput(amount).value;
    const p = parsePercentInput(percent);
    if (p == null) return null;
    return mode === 'adjust' && direction === 'decrease' ? -p : p;
  }, [mode, amount, percent, direction]);

  const plan = useMemo(() => {
    if (value == null) return null;
    return buildBulkCostPlan({ selectedIds, products, mode, value });
  }, [selectedIds, products, mode, value]);

  const skippedMessage = plan ? describeSkipped(plan.skipped) : null;

  const handleApply = async () => {
    if (value == null) {
      toast.warning(mode === 'set' ? 'Informe um custo válido.' : 'Informe um percentual válido.');
      return;
    }
    if (mode === 'percent_of_price' && value <= 0) {
      toast.warning('Informe um percentual maior que zero.');
      return;
    }
    if (!plan || plan.updates.length === 0) {
      toast.warning(skippedMessage ?? 'Nenhum item para atualizar.');
      return;
    }

    setSaving(true);
    let applied = 0;
    let failed = 0;

    // Agrupa por VALOR de custo: no modo "definir o mesmo custo" (o mais usado)
    // isso vira UM único UPDATE para o catálogo inteiro, em vez de uma ida ao
    // servidor por item. Nos modos percentuais os valores diferem, mas itens de
    // mesmo preço/custo ainda se agrupam.
    const idsByCost = new Map<number, string[]>();
    for (const update of plan.updates) {
      const bucket = idsByCost.get(update.cost);
      if (bucket) bucket.push(update.id);
      else idsByCost.set(update.cost, [update.id]);
    }

    for (const [cost, ids] of idsByCost) {
      const { error } = await supabase.from('products').update({ cost }).in('id', ids);
      if (error) failed += ids.length;
      else applied += ids.length;
    }

    setSaving(false);

    if (applied === 0) {
      toast.error('Não foi possível atualizar o custo dos itens.');
      return;
    }

    // Sempre reporta o que ficou de fora — nem truncagem nem falha parcial
    // podem passar como "tudo certo" (CLAUDE.md: no silent caps).
    const parts = [`${applied} ${applied === 1 ? 'item atualizado' : 'itens atualizados'}`];
    if (failed > 0) parts.push(`${failed} falharam`);
    const detail = skippedMessage ? ` ${skippedMessage}` : '';
    if (failed > 0) toast.warning(`${parts.join(', ')}.${detail}`);
    else toast.success(`${parts.join(', ')}.${detail}`);

    onSuccess();
    onOpenChange(false);
    setAmount('');
    setPercent('');
  };

  const preview = plan?.updates.slice(0, 3) ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Definir custo em massa
          </DialogTitle>
          <DialogDescription>
            Aplica o custo nos {selectedIds.length} {selectedIds.length === 1 ? 'item' : 'itens'}{' '}
            selecionados. Em itens com variações, o custo vai para cada variação.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <RadioGroup value={mode} onValueChange={(v) => setMode(v as BulkCostMode)} className="space-y-2">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="set" id="cost-mode-set" />
              <Label htmlFor="cost-mode-set" className="cursor-pointer font-normal">
                Definir o mesmo custo
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="percent_of_price" id="cost-mode-percent" />
              <Label htmlFor="cost-mode-percent" className="cursor-pointer font-normal">
                Custo = % do preço de venda
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="adjust" id="cost-mode-adjust" />
              <Label htmlFor="cost-mode-adjust" className="cursor-pointer font-normal">
                Reajustar o custo atual
              </Label>
            </div>
          </RadioGroup>

          {mode === 'set' ? (
            <div className="space-y-2">
              <Label htmlFor="bulk-cost-amount">Custo</Label>
              {/* Só o modo "definir valor" é dinheiro. A porcentagem abaixo
                  continua com o parser dela — mascarar 60% viraria R$ 0,60. */}
              <MoneyTextInput
                id="bulk-cost-amount"
                value={amount}
                onChange={setAmount}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="bulk-cost-percent">Porcentagem (%)</Label>
              <Input
                id="bulk-cost-percent"
                inputMode="decimal"
                placeholder={mode === 'percent_of_price' ? 'Ex: 60' : 'Ex: 10'}
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
              />
              {mode === 'adjust' && (
                <RadioGroup
                  value={direction}
                  onValueChange={(v) => setDirection(v as 'increase' | 'decrease')}
                  className="flex gap-4 pt-1"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="increase" id="cost-dir-up" />
                    <Label htmlFor="cost-dir-up" className="cursor-pointer font-normal">
                      Aumentar
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="decrease" id="cost-dir-down" />
                    <Label htmlFor="cost-dir-down" className="cursor-pointer font-normal">
                      Diminuir
                    </Label>
                  </div>
                </RadioGroup>
              )}
              {mode === 'percent_of_price' && (
                <p className="text-xs text-muted-foreground">
                  Semeia o catálogo quando você sabe a margem média mas não o custo de cada item.
                  Depois é só corrigir os que fugirem da regra.
                </p>
              )}
            </div>
          )}

          {plan && (
            <div className="space-y-1.5 rounded-lg bg-muted/50 p-3 text-sm">
              <p className="font-medium">
                {plan.updates.length} {plan.updates.length === 1 ? 'item receberá' : 'itens receberão'} custo
              </p>
              {preview.map((u) => (
                <p key={u.id} className="text-xs text-muted-foreground">
                  {u.displayName}: {u.previousCost == null ? 'sem custo' : formatBRL(u.previousCost)} →{' '}
                  {formatBRL(u.cost)}
                </p>
              ))}
              {plan.updates.length > preview.length && (
                <p className="text-xs text-muted-foreground">
                  …e mais {plan.updates.length - preview.length}.
                </p>
              )}
              {skippedMessage && <p className="text-xs text-amber-600">{skippedMessage}</p>}
            </div>
          )}
        </div>

        <DialogFooter>
          {/* Cancelar NUNCA fica desabilitado junto com o confirmar (armadilha
              #41): com a gravação pendurada o usuário ficaria preso no modal. */}
          <Button3D color="gray" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button3D>
          <Button3D onClick={handleApply} disabled={saving || value == null}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Aplicando...
              </>
            ) : (
              'Aplicar custo'
            )}
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
